/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import EntityObserver from './EntityObserver';
import {
  EntityObserverApi,
  EntitySubscription
} from './types';

/**
 * Produces the three entity agnostic observer methods (`addObserver`,
 * `removeObserver`, and `removeAllObservers`) bound to a single subscription
 * type, whereby each method delegates to its EntityObserver counterpart with
 * the given entity applied.
 *
 * This exists so that every entity specific API (AssetObserver, TaskObserver,
 * JobProgressObserver, and JobStatusObserver) declares the delegation once
 * rather than re-implementing it. Entity specific members, such as the
 * `update` utilities of the AssetObserver and TaskObserver, remain declared by
 * each individual API.
 *
 * ```typescript
 *
 *   import createEntityObserver from './createEntityObserver';
 *   import {EntitySubscription} from './types';
 *
 *   const asset = createEntityObserver(EntitySubscription.asset);
 *
 *   export default class AssetObserver {
 *     static addObserver = asset.addObserver;
 *     static removeObserver = asset.removeObserver;
 *     static removeAllObservers = asset.removeAllObservers;
 *   }
 *
 * ```
 *
 * Note, each method invokes EntityObserver at call time rather than holding a
 * reference to its methods, as EntityObserver is exported as a singleton
 * instance whose methods rely on instance state; detaching them would sever
 * that binding.
 *
 * @param   entity The subscription type to which the produced methods are to
 *          be bound.
 * @returns An object exposing the `addObserver`, `removeObserver`, and
 *          `removeAllObservers` methods for the given entity.
 *
 * @see EntityObserver
 */
const createEntityObserver = (entity: EntitySubscription): EntityObserverApi => ({
  addObserver : (ids, observer) => (
    EntityObserver.subscribe(entity, ids, observer)
  ),
  removeObserver : (ids, observer) => (
    EntityObserver.unsubscribe(entity, ids, observer)
  ),
  removeAllObservers : () => (
    EntityObserver.removeAllSubscriptions(entity)
  )
});

export default createEntityObserver;
